import { Injectable } from '@angular/core';

import { ModelNameFlags } from '@era/locations-sdk';

import {
  BrandRestrictionsProfileConfigurationService,
  KioskRestrictionsProfileConfigurationService,
  LocationRestrictionsProfileConfigurationService,
  RoomRestrictionsProfileConfigurationService,
} from '@era/settings-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Category } from '../router/category';
import {
  CategoryRestrictionProfileConfigurationApiActions,
  CategoryRestrictionProfileConfigurationResolverActions,
  CategoryRestrictionProfileConfigurationViewActions,
} from './category-restriction-profile-configuration.actions';
import {
  RestrictionProfileConfiguration,
  restrictionProfileConfigurationFrom,
} from './category-restriction-profile-configuration.state';
import { safeMap } from '../effects.helpers';

@Injectable()
export class CategoryRestrictionProfileConfigurationsEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly brandApi: BrandRestrictionsProfileConfigurationService,
    private readonly locationApi: LocationRestrictionsProfileConfigurationService,
    private readonly roomApi: RoomRestrictionsProfileConfigurationService,
    private readonly kioskApi: KioskRestrictionsProfileConfigurationService
  ) {}

  /**
   * Restriction profile configuration has been selected for a category.
   * Effect: we add the restriction profile configuration to the category via the API.
   */
  restrictionProfileConfigurationSelectedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryRestrictionProfileConfigurationViewActions.restrictionProfileConfigurationSelected),
      mergeMap((action) => {
        const category = action.category;
        return safeMap(
          this.setRestrictionProfileConfigurationsToCategoryEffectBuilder(category, action.profiles),
          (configurations) =>
            CategoryRestrictionProfileConfigurationApiActions.restrictionProfileConfigurationSet({
              category: category,
              configurations,
            }),
          (error) =>
            CategoryRestrictionProfileConfigurationApiActions.restrictionProfileConfigurationSetFailed({ error })
        );
      })
    );
  });

  /**
   * Restriction profile configurations have been requested for a category (resolver).
   * Effect: we load the restriction profile configurations from the API.
   */
  restrictionProfileConfigurationsForCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryRestrictionProfileConfigurationResolverActions.categoryRestrictionProfileConfigurationRequested),
      filter((action) => !!action.category),
      mergeMap((action) => {
        const category = action.category;
        return safeMap(
          this.getRestrictionProfileConfigurationsForCategoryEffectBuilder(category, action.modelNames),
          (apiConfigurations) =>
            CategoryRestrictionProfileConfigurationApiActions.restrictionProfileConfigurationLoaded({
              configurations: apiConfigurations,
            }),
          (error) =>
            CategoryRestrictionProfileConfigurationApiActions.restrictionProfileConfigurationLoadedFailed({ error })
        );
      })
    );
  });

  /**
   * Generic effect builder for setting restriction profile configurations to any category type.
   */
  private setRestrictionProfileConfigurationsToCategoryEffectBuilder(
    category: Category,
    profiles: RestrictionProfileConfiguration[]
  ): Observable<RestrictionProfileConfiguration[]> {
    const requestPayload = {
      restrictionsProfilePresets: profiles.map((x) => ({
        restrictionsProfilePresetId: x.profileId,
        models: x.modelNames,
      })),
    };

    switch (category.categoryType) {
      case 'Brand':
        return this.brandApi
          .putRestrictionsProfileToBrand(category.categoryId, requestPayload)
          .pipe(
            map((configurations) =>
              configurations.map((configuration) => restrictionProfileConfigurationFrom(configuration))
            )
          );
      case 'Location':
        return this.locationApi
          .putRestrictionsProfileToLocation(category.categoryId, requestPayload)
          .pipe(
            map((configurations) =>
              configurations.map((configuration) => restrictionProfileConfigurationFrom(configuration))
            )
          );
      case 'Room':
        return this.roomApi
          .putRestrictionsProfileToRoom(category.categoryId, requestPayload)
          .pipe(
            map((configurations) =>
              configurations.map((configuration) => restrictionProfileConfigurationFrom(configuration))
            )
          );
      case 'Kiosk':
        return this.kioskApi
          .putRestrictionsProfileToKiosk(category.categoryId, requestPayload)
          .pipe(
            map((configurations) =>
              configurations.map((configuration) => restrictionProfileConfigurationFrom(configuration))
            )
          );
    }
  }

  /**
   * Generic effect builder for loading restriction profile configurations for any category type.
   */
  private getRestrictionProfileConfigurationsForCategoryEffectBuilder(
    category: Category,
    modelNames: ModelNameFlags[]
  ): Observable<RestrictionProfileConfiguration[]> {
    switch (category.categoryType) {
      case 'Brand':
        return this.brandApi
          .getRestrictionsProfilesForBrand(category.categoryId, modelNames)
          .pipe(
            map((configurations) =>
              configurations.map((configuration) => restrictionProfileConfigurationFrom(configuration))
            )
          );
      case 'Location':
        return this.locationApi
          .getRestrictionsProfilesForLocation(category.categoryId, modelNames)
          .pipe(
            map((configurations) =>
              configurations.map((configuration) => restrictionProfileConfigurationFrom(configuration))
            )
          );
      case 'Room':
        return this.roomApi
          .getRestrictionsProfilesForRoom(category.categoryId, modelNames)
          .pipe(
            map((configurations) =>
              configurations.map((configuration) => restrictionProfileConfigurationFrom(configuration))
            )
          );
      case 'Kiosk':
        return this.kioskApi
          .getRestrictionsProfilesForKiosk(category.categoryId, modelNames)
          .pipe(
            map((configurations) =>
              configurations.map((configuration) => restrictionProfileConfigurationFrom(configuration))
            )
          );
    }
  }
}
